import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgentActivity } from '@/experiences/sales-portal/domain/superintelligence/components/agent-activity';
import { AgentApprovalBanners } from '@/experiences/sales-portal/domain/superintelligence/components/agent-approval-banner';
import { AgentComposer } from '@/experiences/sales-portal/domain/superintelligence/components/agent-composer';
import { AgentMessageBubble } from '@/experiences/sales-portal/domain/superintelligence/components/agent-message-bubble';
import { AgentThinkingIndicator } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thinking-indicator';
import { AgentThreadSkeleton } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-skeleton';
import { AgentThreadStatusBanner } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-status-banner';
import { Animated, ScrollView, StatusBar, View } from 'react-native';
import { Banner, ChatSystemMessage } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { isAwaitingOutput } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-message-parts';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { threadSegments } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-thread-segments';
import { useAuiState } from '@assistant-ui/store';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { useQuickReturnHeaderScroll } from '@/experiences/sales-portal/domain/superintelligence/blocs/hooks/use-quick-return-header-scroll';
import { useSuperintelligenceContext } from '@/experiences/sales-portal/domain/superintelligence/containers/superintelligence-runtime-provider';

const CONTENT_MAX_WIDTH = 750;

/** Reads messages from assistant-ui's store rather than its DOM primitives so it renders on native too. */
export const SuperintelligenceThread: React.FC = () => {
  const [styles] = useStyles();
  const scrollRef = useRef<ScrollView>(null);
  const { dismissError, error, isLoadingThread } =
    useSuperintelligenceContext();
  const messages = useAuiState((s) => s.thread.messages);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  // On phones the header overlays the scroll content and the tab bar floats over the body, so the thread pads
  // for both. The bottom inset sits on a wrapper because KeyboardAvoidingView writes its own paddingBottom.
  const { handleScroll, headerHiddenBy, headerInset } =
    useQuickReturnHeaderScroll();
  const bottomNavHeight = useExperienceUIStore(
    (state) => state.bottomNavHeight,
  );
  const bottomInset = useIsMobileBottomNavLayout() ? bottomNavHeight : 0;
  // KeyboardAvoidingView measures against its parent, so it needs its own distance from the top of the screen
  // to know how far the keyboard overlaps it. Android measures from below the status bar, so that is added back.
  const avoiderRef = useRef<View>(null);
  const [avoiderWindowTop, setAvoiderWindowTop] = useState(0);
  const handleAvoiderLayout = useCallback(() => {
    avoiderRef.current?.measureInWindow?.((_x, y) => {
      setAvoiderWindowTop(y);
    });
  }, []);

  const segments = threadSegments(messages, isRunning);
  const lastSegment = segments[segments.length - 1];
  const awaitingOutput = isAwaitingOutput(isRunning, messages);
  const showThinking = awaitingOutput && lastSegment?.kind !== 'activity';

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
  }, []);
  useEffect(() => {
    scrollToEnd();
  }, [messages, awaitingOutput, scrollToEnd]);

  return (
    <View style={[styles.container, { paddingBottom: bottomInset }]}>
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={
          avoiderWindowTop + (StatusBar.currentHeight ?? 0)
        }
        onLayout={handleAvoiderLayout}
        ref={avoiderRef}
        style={styles.avoider}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: headerInset },
          ]}
          keyboardShouldPersistTaps="handled"
          onScroll={handleScroll}
          ref={scrollRef}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          style={styles.scroll}
        >
          {error ? (
            <View style={styles.errorBanner}>
              <Banner tone="error">
                <Banner.Description>{error}</Banner.Description>
                <Banner.Dismiss
                  label={i18n.t('dismiss')}
                  onPress={dismissError}
                />
              </Banner>
            </View>
          ) : null}
          {isLoadingThread ? (
            <AgentThreadSkeleton style={styles.loading} />
          ) : messages.length === 0 ? (
            <ChatSystemMessage>
              {i18n.t('sales_portal.superintelligence.empty')}
            </ChatSystemMessage>
          ) : null}
          {segments.map((segment, index) => {
            const previous = segments[index - 1];
            const isUser =
              segment.kind === 'bubble' && segment.message.role === 'user';
            const previousIsUser =
              previous?.kind === 'bubble' && previous.message.role === 'user';
            // Same rule as the copilot: bubbles on the same side sit tighter and only the first shows a tail
            const grouped = previous !== undefined && previousIsUser === isUser;
            const spacing = grouped
              ? styles.messageGrouped
              : styles.messageSeparated;

            if (segment.kind === 'activity') {
              return (
                <View key={segment.key} style={[styles.activityRow, spacing]}>
                  <AgentActivity
                    entries={segment.entries}
                    live={isRunning && segment === lastSegment}
                  />
                </View>
              );
            }
            return (
              <AgentMessageBubble
                key={segment.key}
                message={segment.message}
                parts={segment.parts}
                showTail={previous?.kind !== 'bubble' || !grouped}
                style={spacing}
              />
            );
          })}
          {showThinking ? (
            <View
              accessibilityLabel={i18n.t(
                'sales_portal.superintelligence.typing',
              )}
              aria-busy
              style={[styles.typingRow, styles.messageSeparated]}
            >
              <AgentThinkingIndicator />
            </View>
          ) : null}
          <AgentApprovalBanners style={styles.messageSeparated} />
          <AgentThreadStatusBanner style={styles.messageSeparated} />
        </ScrollView>
        <View style={styles.composer}>
          <AgentComposer />
        </View>
      </KeyboardAvoidingView>
      {headerInset > 0 ? (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.headerBackdrop,
            {
              height: headerInset,
              transform: [
                { translateY: Animated.multiply(headerHiddenBy, -1) },
              ],
            },
          ]}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  activityRow: {
    width: '100%',
  },
  avoider: {
    alignItems: 'center',
    flex: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  composer: {
    marginBottom: theme.sk.spacing.spaceMedium,
    marginTop: theme.sk.spacing.spaceSmall,
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
  },
  container: {
    flex: 1,
  },
  errorBanner: {
    paddingTop: theme.sk.spacing.spaceSmall,
    width: '100%',
  },
  headerBackdrop: {
    backgroundColor: theme.sk.color.bg.primary.default,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  loading: {
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  messageGrouped: {
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  messageSeparated: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  scroll: {
    flex: 1,
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  typingRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
}));
